/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/naming-convention */

import { ResponseType } from 'axios';
import {
  AIGeneratedSong,
  CustomizableSong,
  FreeSong,
  HTTPError,
  PaginatedList,
  PaginationData,
  PrivateUser,
  User,
  UserBasicData,
} from '../../../entities';
import { handleErrors } from '../../../helpers';
import {
  LibrarySongListResponse,
  LibrarySongResponse,
  PrivateUserResponse,
  QueryParameters,
} from '../../../responses/responseTypes';
import { UserStorage } from '../../../storage/user/userStorage';
import { Client } from '../../client';
import type { AuthorizationData } from '../../repositories/authRepository';
import { AuthRepository } from '../../repositories/authRepository';
import { UserRepository } from '../../repositories/userRepository';
import { AuthDataProvider } from '../auth';
import { transformToError } from './transformers';

export default class UserRepositoryImpl implements UserRepository {
  client: Client;

  authRepository: AuthRepository;

  userStorage: UserStorage;

  constructor(
    client: Client,
    authRepository: AuthRepository,
    userStorage: UserStorage
  ) {
    this.client = client;
    this.authRepository = authRepository;
    this.userStorage = userStorage;
  }

  async registerAndLogin(
    email: string,
    password: string,
    cloudflareToken: string
  ): Promise<User> {
    const { authRepository } = this;
    return new Promise((resolve, reject) => {
      authRepository
        .register(email, password, cloudflareToken)
        .then(() => {
          resolve(this.getSessionUser());
        })
        .catch((err) => {
          const error = handleErrors(err);
          reject(error);
        });
    });
  }

  async checkEmailAvailability(email: string): Promise<boolean> {
    const { client } = this;
    return new Promise((resolve, reject) => {
      client
        .post<{ status: string }>('/user/check_email', {
          email,
        })
        .then((response) => {
          // the status returned from backend can be either "ok" or "used"
          const status = response.data.status === 'ok';

          resolve(status);
        })
        .catch((err) => {
          const error = handleErrors(err);
          reject(error);
        });
    });
  }

  async requestResetPassword(email: string): Promise<string> {
    const { client } = this;
    return new Promise((resolve, reject) => {
      client
        .post<{ success: string }>('/password/request', {
          email,
        })
        .then((response) => {
          const successMessage = response.data.success;
          resolve(successMessage);
        })
        .catch((err) => {
          const error = handleErrors(err);
          reject(error);
        });
    });
  }

  async submitNewPassword(token: string, password: string): Promise<string> {
    const { client } = this;
    return new Promise((resolve, reject) => {
      client
        .post<{ success: string }>('/password/reset', {
          token,
          password,
        })
        .then((response) => {
          const successMessage = response.data.success;
          resolve(successMessage);
        })
        .catch((err) => {
          const error = handleErrors(err);
          reject(error);
        });
    });
  }

  async login(): Promise<User | null> {
    const { userStorage, authRepository } = this;
    const userData = userStorage.getUserData();
    const refreshToken = authRepository.getRefreshToken();
    if (!userData || !refreshToken) {
      return null;
    }

    const accessToken = authRepository.getAccessToken();
    if (refreshToken && !accessToken) {
      return authRepository.updateToken().then(() => this.getSessionUser());
    }

    return this.getSessionUser();
  }

  async loginWithCredentials(
    email: string,
    password: string,
    cloudflareToken: string
  ): Promise<User> {
    const data = AuthDataProvider.credentials(email, password, cloudflareToken);
    return this.authorize(data);
  }

  async loginWithGoogle(tokenId: string): Promise<User> {
    const data = AuthDataProvider.google(tokenId);
    return this.authorize(data);
  }

  async loginWithFacebook(accessToken: string): Promise<User> {
    const data = AuthDataProvider.facebook(accessToken);
    return this.authorize(data);
  }

  logout(): void {
    const { authRepository, userStorage } = this;
    userStorage.deleteUserData();
    authRepository.deleteToken();
  }

  async getSessionUser(): Promise<User> {
    const { client, userStorage } = this;
    return new Promise((resolve, reject) => {
      client
        .get<PrivateUserResponse>('/users/me')
        .then((response) => {
          const privateUser = new PrivateUser(response.data);
          userStorage.saveUserData(privateUser);
          resolve(privateUser);
        })
        .catch((err) => {
          const error = handleErrors(err);
          reject(error);
        });
    });
  }

  async authorize(data: AuthorizationData): Promise<User> {
    const { authRepository } = this;
    return new Promise((resolve, reject) => {
      authRepository
        .authorize(data)
        .then(() => {
          resolve(this.getSessionUser());
        })
        .catch((err) => {
          const error = handleErrors(err);
          reject(error);
        });
    });
  }

  async update(id: string, userData: UserBasicData): Promise<User> {
    const { client, userStorage } = this;
    return new Promise((resolve, reject) => {
      client
        .patch<PrivateUserResponse>(`/users/${id}`, userData.toSearchParams())
        .then((response) => {
          const privateUser = new PrivateUser(response.data);
          userStorage.saveUserData(privateUser);
          resolve(privateUser);
        })
        .catch((err) => {
          const error = handleErrors(err);
          reject(error);
        });
    });
  }

  async deleteAccount(): Promise<string> {
    const { client, authRepository, userStorage } = this;
    return new Promise((resolve, reject) => {
      client
        .delete('/users/me')
        .then((response) => {
          // @ts-ignore
          const { message } = response.data;
          return message;
        })
        .then((message) => {
          userStorage.deleteUserData();
          authRepository.deleteToken();
          resolve(message);
        })
        .catch((err) => {
          const error = handleErrors(err);
          reject(error);
        });
    });
  }

  async getSongsLibrary(
    queryParameters: QueryParameters
  ): Promise<PaginatedList<FreeSong | CustomizableSong | AIGeneratedSong>> {
    const { client } = this;
    return new Promise((resolve, reject) => {
      client
        .get<LibrarySongListResponse>(`/songs/library`, {
          params: queryParameters,
        })
        .then((response) => {
          const songList = response.data.items.map(
            (data: LibrarySongResponse) => {
              // eslint-disable-next-line camelcase
              const { song, vega_song } = data;
              if (song) {
                if (song.is_free) {
                  return new FreeSong(song);
                }
                return new CustomizableSong(song);
              }
              // eslint-disable-next-line camelcase
              if (vega_song) {
                return new AIGeneratedSong(vega_song);
              }
              throw HTTPError.invalidData();
            }
          );
          const { pagination_data: paginationData } = response.data;
          resolve(
            new PaginatedList(songList, new PaginationData(paginationData))
          );
        })
        .catch((err) => {
          const error = handleErrors(err);
          reject(error);
        });
    });
  }

  async downloadSongLicensePDF(songId: string): Promise<{
    url: string;
    filename: string;
  }> {
    const { client } = this;
    const config = {
      responseType: 'blob' as ResponseType,
    };
    return new Promise((resolve, reject) => {
      client
        .get<string>(`/songs/${songId}/license/pdf`, config)
        .then((response) => {
          const url = URL.createObjectURL(
            new Blob([response.data], {
              type: 'application/pdf',
            })
          );
          const filename = this.getSongLicensePDFFilename(response);
          resolve({
            url,
            filename,
          });
        })
        .catch((error) => {
          error.response.data.text().then((errorResponse: string) => {
            const errorObj = JSON.parse(errorResponse);
            reject(transformToError(errorObj));
          });
        });
    });
  }

  // eslint-disable-next-line class-methods-use-this
  getSongLicensePDFFilename(response: any): string {
    try {
      const headerLine = response.headers['content-disposition'];
      const startFileNameIndex = headerLine.indexOf('"') + 1;
      const endFileNameIndex = headerLine.lastIndexOf('"');
      return headerLine.substring(startFileNameIndex, endFileNameIndex);
    } catch {
      // as a fallback (if e.g no Content-Disposition header is found)
      // we use the license.pdf filename instead of showing an unnecessary error
      return 'license.pdf';
    }
  }

  resendConfirmationEmail(): Promise<boolean> {
    const { client } = this;
    return new Promise((resolve) => {
      client
        .post(`/users/email/send-confirm-link`)
        .then(() => {
          resolve(true);
        })
        .catch(() => {
          resolve(false);
        });
    });
  }
}
